import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import UIButton from "./Button";
import PremiumPopup from "./PremiumPopup";
import ProfilePopup from "./ProfilePopup";
import useAIModels from "../hooks/useAIModels";
import { storage } from "../utils/storage";
import { useUIStore } from "../store/uiStore";
import { useUserTier } from "../hooks/useUserTier";

const navItems = [
  {
    id: "home",
    label: "Home",
    path: "/",
    icon: "/assets/images/icon-home.svg",
    selectedIcon: "/assets/images/icon-home-active.svg",
  },
  {
    id: "recent",
    label: "Recent",
    path: "/recent",
    icon: "/assets/images/icon-recent.svg",
    selectedIcon: "/assets/images/icon-recent-active.svg",
  },
  {
    id: "premium",
    label: "Premium",
    path: "/pricing",
    icon: "/assets/images/icon-premium.svg",
    selectedIcon: "/assets/images/icon-premium-active.svg",
  },
  {
    id: "profile",
    label: "Profile",
    path: "/profile",
    icon: "/assets/images/icon-profile.svg",
    selectedIcon: "/assets/images/icon-profile-active.svg",
  },
];

interface NavBarProps {
  onNavbarToggle?: (isOpen: boolean) => void;
}

const NavBar: React.FC<NavBarProps> = ({ onNavbarToggle }) => {
  const [isPremiumPopupOpen, setIsPremiumPopupOpen] = useState(false);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { models } = useAIModels();
  const { isModelDropdownOpen } = useUIStore();
  const userTier = useUserTier();
  // Only show upgrade banner for FREE users
  const showUpgradeBanner = userTier === 'free';

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (!mobile && !tablet) {
        setIsMobileMenuOpen(false);
        if (onNavbarToggle) onNavbarToggle(false);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, [onNavbarToggle]);

  const handleOpenPremiumPopup = () => {
    setIsPremiumPopupOpen(true);
  };

  const handleClosePremiumPopup = () => {
    setIsPremiumPopupOpen(false);
  };

  const handleOpenProfilePopup = () => {
    setIsProfilePopupOpen(true);
  };

  const handleCloseProfilePopup = () => {
    setIsProfilePopupOpen(false);
  };

  const toggleNavbar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    if (onNavbarToggle) onNavbarToggle(newState);
  };

  const handleNavItemClick = (itemId: string, event: React.MouseEvent) => {
    console.log('🟣 [Navbar.tsx] Nav item clicked:', itemId);
    if (itemId === "premium") {
      console.log('🟣 [Navbar.tsx] Premium clicked, opening popup');
      event.preventDefault();
      handleOpenPremiumPopup();
    }
    if (itemId === "profile") {
      event.preventDefault();
      handleOpenProfilePopup();
    }
    if (isMobile || isTablet) {
      setIsMobileMenuOpen(false);
      if (onNavbarToggle) onNavbarToggle(false);
    }
  };

  const handleNewChat = () => {
    const token = storage.getJwtToken();
    if (!token) {
      navigate("/get-started");
      return;
    }

    const modelToUse = models.length > 0 ? models[0] : null;
    if (modelToUse) {
      navigate(`/chat/${modelToUse.id}`);
    } else {
      navigate("/");
    }
  };

  const handleLogoClick = () => {
    navigate("/");
    if (isMobile || isTablet) {
      setIsMobileMenuOpen(false);
      if (onNavbarToggle) onNavbarToggle(false);
    }
  };

  const handleCloseIconClick = () => {
    setIsMobileMenuOpen(false);
    if (onNavbarToggle) onNavbarToggle(false);
    navigate("/");
  };

  if (isMobile || isTablet) {
    const isChatPage = location.pathname.includes("/chat");

    return (
      <>
        {!isChatPage && (
          <div
            className={`mobile-top-navbar fixed top-0 left-0 right-0 z-40 flex items-center justify-between p-4 m-2 rounded-xl ${
              isMobileMenuOpen ? "bg-grey" : "bg-light-black"
            }`}>
            <div
              className="flex items-center cursor-pointer"
              onClick={handleLogoClick}>
              <img
                src="/assets/images/logo.svg"
                alt="MixxAI"
                className="w-8 h-8 mr-2"
              />
            </div>

            {location.pathname === "/recent" && !isMobileMenuOpen && (
              <div className="flex items-center justify-center gap-3">
                <h2 className="text-white text-lg font-medium">Recent chat</h2>
                <div
                  className="cursor-pointer flex items-center justify-center"
                  onClick={handleNewChat}>
                  <img
                    src="/assets/images/icon-new-chat.svg"
                    alt="New chat"
                    className="w-8 h-8"
                  />
                </div>
              </div>
            )}

            <div className="mobile-navbar-toggle" onClick={toggleMobileMenu}>
              <img
                src={
                  isMobileMenuOpen
                    ? "/assets/images/icon-close.svg"
                    : "/assets/images/icon-burger.svg"
                }
                alt={isMobileMenuOpen ? "Close menu" : "Open menu"}
                className="w-6 h-6"
              />
            </div>
          </div>
        )}

        {isChatPage && (
          <div
            className={`fixed top-4 left-6 z-40 cursor-pointer translate-y-3/4 ${
              isModelDropdownOpen ? "hidden sm:block" : ""
            }`}
            onClick={handleCloseIconClick}>
            <img
              src={"/assets/images/icon-close.svg"}
              alt={"Close menu"}
              className="w-6 h-6"
            />
          </div>
        )}

        {isMobileMenuOpen && (
          <div>
            <div className="fixed inset-0 bg-black z-30"></div>
            <div className="mobile-navbar-menu fixed inset-0 bg-grey z-40 flex flex-col justify-between overflow-auto pt-16 m-2 rounded-xl">
              <div className="flex flex-col items-center m-4 gap-2">
                {navItems.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    onClick={(e) => handleNavItemClick(item.id, e)}
                    className={({ isActive }) =>
                      `mobile-nav-item w-full text-center ${
                        isActive ? "active" : ""
                      }`
                    }>
                    {({ isActive }) => {
                      const isPremiumActive =
                        item.id === "premium" && isPremiumPopupOpen;
                      const showActive =
                        item.id === "premium" ? isPremiumActive : isActive;

                      return (
                        <div className="flex items-center gap-2 p-2">
                          <img
                            src={showActive ? item.selectedIcon : item.icon}
                            alt={item.label}
                            className="w-8 h-8 mb-2"
                          />
                          <p
                            className={`text-lg ${
                              showActive ? "text-btn-color" : "text-white"
                            }`}>
                            {item.label}
                          </p>
                        </div>
                      );
                    }}
                  </NavLink>
                ))}
              </div>

              <div>
                {showUpgradeBanner && (
                  <div
                    className={
                      isTablet
                        ? "flex rounded-xl mx-4 p-4 mb-4"
                        : "rounded-xl mx-4 p-4 mb-4"
                    }
                    style={{
                      backgroundImage:
                        "url('/assets/images/bg-premium-responsive.jpg')",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}>
                    <img
                      src="/assets/images/icon-premium-page.svg"
                      alt="icon-premium"
                    />
                    {isTablet ? (
                      <div className="flex flex-col ml-4">
                        <p className="text-white text-left text-2xl font-bold my-4">
                          Upgrade 🚀
                          <br />
                          to Premium
                        </p>
                        <p className="text-white text-left text-sm font-normal mb-4">
                          Access to Advance AI, Higher words limit, Remove ads and
                          more
                        </p>
                        <UIButton
                          text="Upgrade"
                          backgroundColor="btn-color"
                          onClick={handleOpenPremiumPopup}
                          textColor={""}
                          classButton="w-28"
                        />
                      </div>
                    ) : (
                      <>
                        <p className="text-white text-left text-2xl font-bold my-4">
                          Upgrade 🚀
                          <br />
                          to Premium
                        </p>
                        <p className="text-white text-left text-sm font-normal mb-4">
                          Access to Advance AI, Higher words limit, Remove ads and
                          more
                        </p>
                        <UIButton
                          text="Upgrade"
                          backgroundColor="btn-color"
                          onClick={handleOpenPremiumPopup}
                          textColor={""}
                        />
                      </>
                    )}
                  </div>
                )}

                <div className="text-color-desc text-left pl-4 pb-4 pt-1">
                  <p>WitWorkApp© </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <PremiumPopup
          isOpen={isPremiumPopupOpen}
          onClose={handleClosePremiumPopup}
        />
        <ProfilePopup
          isOpen={isProfilePopupOpen}
          onClose={handleCloseProfilePopup}
        />
      </>
    );
  }

  return (
    <nav
      className={`nav-bar ${
        isCollapsed ? "w-20" : "w-1/5"
      } transition-all duration-300`}
      style={{ padding: isCollapsed ? "8px items-center" : "16px" }}>
      <div className="nav-logo cursor-pointer" onClick={handleLogoClick}>
        <img src="/assets/images/logo.svg" alt="MixxAI" />
      </div>

      {navItems.map((item) => (
        <NavLink
          key={item.id}
          to={item.path}
          onClick={(e) => handleNavItemClick(item.id, e)}
          className={({ isActive }) =>
            `nav-item ${isActive ? "active" : ""} ${
              isCollapsed ? "justify-center" : ""
            }`
          }
          style={{ padding: isCollapsed ? "8px" : "16px" }}>
          {({ isActive }) => {
            const isPremiumActive = item.id === "premium" && isPremiumPopupOpen;
            const showActive =
              item.id === "premium" ? isPremiumActive : isActive;

            return (
              <>
                <img
                  src={showActive ? item.selectedIcon : item.icon}
                  alt={item.label}
                />
                {!isCollapsed && (
                  <p
                    className={showActive ? "selected" : ""}
                    style={{ fontSize: 16 }}>
                    {item.label}
                  </p>
                )}
              </>
            );
          }}
        </NavLink>
      ))}

      {!isCollapsed && showUpgradeBanner && (
        <div
          className="rounded-xl absolute bottom-16 left-4 right-4 p-4"
          style={{
            backgroundImage: "url('/assets/images/bg-premium.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            minHeight: 313,
          }}>
          <img src="/assets/images/icon-premium-page.svg" alt="icon-premium" />
          <p className="text-white text-left text-2xl font-bold my-4">
            Upgrade 🚀
            <br />
            to Premium
          </p>
          <p className="text-white text-left text-sm font-normal mb-4">
            Access to Advance AI, Higher words limit, Remove ads and more
          </p>
          <UIButton
            text="Upgrade"
            backgroundColor="btn-color"
            onClick={handleOpenPremiumPopup}
            textColor={""}
          />
        </div>
      )}
      <div
        className={`text-color-desc absolute bottom-6 flex justify-between w-full pr-8`}>
        {!isCollapsed && <p>WitWorkApp© </p>}
        <div
          onClick={toggleNavbar}
          className="cursor-pointer"
          style={{ margin: isCollapsed ? "0 auto" : "" }}>
          <img
            src={
              isCollapsed
                ? "/assets/images/icon-navbar-open.svg"
                : "/assets/images/icon-navbar-close.svg"
            }
            alt={isCollapsed ? "Open navbar" : "Close navbar"}
          />
        </div>
      </div>

      <PremiumPopup
        isOpen={isPremiumPopupOpen}
        onClose={handleClosePremiumPopup}
      />
      <ProfilePopup
        isOpen={isProfilePopupOpen}
        onClose={handleCloseProfilePopup}
      />
    </nav>
  );
};

export default NavBar;
